import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { ScreenLayout, Header, FormHeader } from '../components';
import { ScopedListBanner } from '../components/data-lists/ScopedListBanner';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { usePermissions } from '../hooks/usePermissions';
import { useLocale } from '../contexts/LocaleContext';
import { CURRENCIES, getCurrencySymbol } from '../utils/currencies';
import { countActive, FilterField, QuickView, relativeRange, SortOption, useListFilters } from '../components/listFilters/listFilters';
import { FilterSheet } from '../components/listFilters/FilterSheet';
import { QuotationDocumentModal } from '../components/documents/QuotationDocumentModal';
import { ListFilterBar } from '../components/listFilters/ListFilterBar';
import { searchProducts, searchVendors } from '../components/listFilters/entitySearch';
import { useAppStore } from '../store/appStore';
import { businessCurrency } from '../utils/businessDefaults';
import { buildQuotationListQuery, quotationExpiringRange, QUOTATION_SORTS } from '../utils/quotationListFilters';

interface QuotationsListScreenProps {
  scopedToDataListId?: string;
  scopedListName?: string;
}

const STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired', 'converted'] as const;

const STATUS_COLORS: Record<string, string> = {
  draft: '#6B7280',
  sent: '#2563EB',
  accepted: '#059669',
  rejected: '#DC2626',
  expired: '#D97706',
  converted: '#7C3AED',
};

const STATUS_ICONS: Record<string, string> = {
  draft: 'create-outline',
  sent: 'paper-plane-outline',
  accepted: 'checkmark-circle-outline',
  rejected: 'close-circle-outline',
  expired: 'time-outline',
  converted: 'swap-horizontal-outline',
};

export const QuotationsListScreen: React.FC<QuotationsListScreenProps> = ({ scopedToDataListId, scopedListName }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t, locale } = useLocale();
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  // Parent notification updates must not restart a list request.
  const notify = useRef(showNotification);
  notify.current = showNotification;

  const [quotations, setQuotations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [documentsFor, setDocumentsFor] = useState<any | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number> | undefined>();
  const [members, setMembers] = useState<Array<{ id: string; name: string }>>([]);
  const businessId = useAppStore(s => String(s.currentBusiness?._id || (s.currentBusiness as any)?.id || ''));
  const businessCurrencyCode = useAppStore(s => s.currentBusiness?.currency);
  const requestId = useRef(0);
  const loadingRef = useRef(false);
  const loadedQuery = useRef<string | null>(null);
  // Team members for "created by", loaded once per business.
  useEffect(() => {
    let cancelled = false;
    setMembers([]);
    if (!businessId) return;
    void apiService.getBusinessUsers(businessId).then(response => {
      if (cancelled || response.statusCode !== 1000 || !Array.isArray(response.data)) return;
      setMembers(response.data.filter(m => m.userStatus === 'active' && m.user)
        .map(m => ({ id: m.userId, name: [m.user.firstName, m.user.lastName].filter(Boolean).join(' ') || m.user.username || m.user.email })));
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [businessId]);

  const statusLabel = (status: string) => {
    const key = `contactEdit.status.quotation.${status}` as any;
    const label = t(key);
    return label === key ? status : label;
  };

  // Filters — the shared list filters: remembered per screen, quick views + saved views.
  const quickViews = useMemo<QuickView[]>(() => [
    { key: 'drafts', label: t('quotations.view.drafts'), icon: 'create-outline', filters: { statuses: ['draft'] } },
    { key: 'sent', label: t('quotations.view.awaiting'), icon: 'paper-plane-outline', filters: { statuses: ['sent'] } },
    { key: 'toConvert', label: t('quotations.view.toConvert'), icon: 'swap-horizontal-outline', filters: { statuses: ['accepted'], converted: 'no' } },
    { key: 'expiring', label: t('quotations.view.expiringSoon'), icon: 'hourglass-outline', filters: { validity: 'valid', validUntil: quotationExpiringRange() }, sort: 'expiringSoon' },
    { key: 'month', label: t('quotations.view.thisMonth'), icon: 'calendar-outline', filters: { createdAt: relativeRange.thisMonth() } },
  ], [t]);
  const list = useListFilters(scopedToDataListId ? `quotations:${scopedToDataListId}` : 'quotations', { quickViews });
  // Keep selected filters editable even when the matching page is empty or members are still loading.
  const currencies = useMemo(() => [...new Set([
    businessCurrencyCode, list.state.filters.currency, ...CURRENCIES.map(c => c.code), ...quotations.map(q => q.currency),
  ].filter((code): code is string => typeof code === 'string' && !!code).map(code => code.toUpperCase()))],
  [businessCurrencyCode, list.state.filters.currency, quotations]);
  const creatorOptions = useMemo(() => {
    const options = members.map(m => ({ value: m.id, label: m.name }));
    const selected = list.state.filters.createdBy;
    if (selected && !options.some(option => option.value === selected)) options.push({ value: selected, label: selected });
    return options;
  }, [members, list.state.filters.createdBy]);
  const quotationFields = useMemo<FilterField[]>(() => [
    { key: 'statuses', label: t('quotations.filters.status'), type: 'multi', section: t('quotations.filterSection.quotation'), hint: t('quotations.filters.statusHint'),
      options: STATUSES.map((s) => ({ value: s, label: statusCounts ? `${statusLabel(s)} (${statusCounts[s] || 0})` : statusLabel(s), color: STATUS_COLORS[s] })) },
    { key: 'createdAt', label: t('quotations.filters.created'), type: 'date', section: t('quotations.filterSection.quotation') },
    { key: 'validity', label: t('quotations.filters.validity'), type: 'choice', section: t('quotations.filterSection.quotation'), options: (['valid', 'expired', 'none'] as const).map((v) => ({ value: v, label: t(`quotations.filters.validity.${v}` as any) })) },
    { key: 'validUntil', label: t('quotations.filters.validUntil'), type: 'date', section: t('quotations.filterSection.quotation') },
    { key: 'total', label: t('quotations.filters.total'), type: 'range', unit: businessCurrencyCode || undefined, section: t('quotations.filterSection.quotation') },
    { key: 'converted', label: t('quotations.filters.converted'), type: 'choice', section: t('quotations.filterSection.quotation'), options: [{ value: 'yes', label: t('quotations.filters.convertedYes') }, { value: 'no', label: t('quotations.filters.convertedNo') }] },
    { key: 'products', label: t('quotations.filters.products'), type: 'entity', search: searchProducts, section: t('quotations.filterSection.items') },
    { key: 'vendors', label: t('quotations.filters.vendors'), type: 'entity', search: searchVendors, section: t('quotations.filterSection.items'), hint: t('quotations.filters.vendorsHint') },
    { key: 'createdBy', label: t('quotations.filters.createdBy'), type: 'choice', section: t('quotations.filterSection.team'), options: creatorOptions },
    { key: 'currency', label: t('quotations.filters.currency'), type: 'choice', section: t('quotations.filterSection.team'), options: currencies.map((c) => ({ value: c, label: c })) },
  ], [t, statusCounts, creatorOptions, currencies, businessCurrencyCode]);
  const quotationSorts = useMemo<SortOption[]>(() => [
    { key: 'newest', label: t('quotations.filters.sort.newest'), ...QUOTATION_SORTS.newest, icon: 'arrow-down-outline' },
    { key: 'oldest', label: t('quotations.filters.sort.oldest'), ...QUOTATION_SORTS.oldest, icon: 'arrow-up-outline' },
    { key: 'totalDesc', label: t('quotations.filters.sort.totalDesc'), ...QUOTATION_SORTS.totalDesc, icon: 'trending-up-outline' },
    { key: 'totalAsc', label: t('quotations.filters.sort.totalAsc'), ...QUOTATION_SORTS.totalAsc, icon: 'trending-down-outline' },
    { key: 'expiringSoon', label: t('quotations.filters.sort.expiringSoon'), ...QUOTATION_SORTS.expiringSoon, icon: 'hourglass-outline' },
    { key: 'updated', label: t('quotations.sort.updated'), ...QUOTATION_SORTS.updated, icon: 'time-outline' },
  ], [t]);
  const activeFilterCount = countActive(quotationFields, list.state);
  const hasSearchOrFilters = activeFilterCount > 0 || !!list.state.search.trim();
  const query = useMemo(() => buildQuotationListQuery(list.state), [list.state.filters, list.state.sort, list.state.search]);
  const queryKey = JSON.stringify([businessId, scopedToDataListId, query]);

  const loadQuotations = useCallback(async (page: number = 0, reset: boolean = false) => {
    if (!businessId || !list.ready || (!reset && loadingRef.current)) return;
    const currentRequest = ++requestId.current;
    loadingRef.current = true;
    setLoading(true);
    try {
      const response = await apiService.getQuotations({
        limit: 20,
        offset: page * 20,
        ...query,
        dataListId: scopedToDataListId,
      });
      if (currentRequest !== requestId.current) return;
      if (response.statusCode === 1000 && response.data) {
        const newQuotations = response.data.quotations || [];
        setQuotations((prev) => (reset ? newQuotations : [...prev, ...newQuotations]));
        setTotal(response.data.pagination?.total || 0);
        setStatusCounts(response.data.statusCounts);
        setHasMore((page * 20) + newQuotations.length < (response.data.pagination?.total || 0));
        setCurrentPage(page);
        loadedQuery.current = queryKey;
      } else {
        notify.current('error', t('common.error'), response.errors?.[0]?.message || t('quotations.list.loadError'));
      }
    } catch (error: any) {
      if (currentRequest === requestId.current) notify.current('error', t('common.error'), error.message || t('quotations.list.loadError'));
    } finally {
      if (currentRequest === requestId.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [businessId, scopedToDataListId, list.ready, query, queryKey, t]);

  // Reload whenever the filters change (search debounced), once remembered filters are read.
  useEffect(() => {
    // Invalidate pending pages immediately, including during the search debounce.
    requestId.current += 1;
    loadedQuery.current = null;
    loadingRef.current = true;
    setLoading(true);
    setQuotations([]);
    setTotal(0);
    setStatusCounts(undefined);
    setHasMore(false);
    setCurrentPage(0);
    if (!list.ready || !businessId) {
      if (!businessId) { loadingRef.current = false; setLoading(false); }
      return;
    }
    const timer = setTimeout(() => loadQuotations(0, true), list.state.search ? 400 : 0);
    return () => {
      clearTimeout(timer);
      requestId.current += 1;
      loadingRef.current = false;
    };
  }, [loadQuotations, list.ready]);

  const handleLoadMore = () => {
    if (!loadingRef.current && !loading && hasMore && loadedQuery.current === queryKey) void loadQuotations(currentPage + 1, false);
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const formatDate = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTotal = (amount: number, currency?: string) =>
    `${getCurrencySymbol(currency || businessCurrency())} ${Number(amount || 0).toFixed(2)}`;

  const copyNumber = async (value: string) => {
    try {
      if (Platform.OS === 'web' && navigator.clipboard) await navigator.clipboard.writeText(value);
      else await Clipboard.setStringAsync(value);
      showNotification('success', t('orders.msg.copied'), t('quotations.list.numberCopied'));
    } catch {
      showNotification('error', t('common.error'), t('orders.msg.copyFailed'));
    }
  };

  const renderQuotation = ({ item }: { item: any }) => {
    const color = STATUS_COLORS[item.quotationStatus] || STATUS_COLORS.draft;
    const itemsCount = Array.isArray(item.items) ? item.items.length : 0;
    const validUntil = formatDate(item.validUntil);
    const isExpired = item.validUntil && new Date(item.validUntil).getTime() < Date.now() && !['accepted', 'converted', 'rejected'].includes(item.quotationStatus);
    return (
      <TouchableOpacity style={styles.card} onPress={() => navigateTo('quotation-detail', item._id)} activeOpacity={0.7}>
        <View style={[styles.cardHeader, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.cardHeaderStart}>
            <View style={[styles.numberRow, { flexDirection: rtl.rowDirection }]}>
              <Text style={styles.number} numberOfLines={1}>{item.quotationNumber}</Text>
              <TouchableOpacity style={styles.copyButton} onPress={() => copyNumber(item.quotationNumber)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name="copy-outline" size={16} color={colors.primary as string} />
              </TouchableOpacity>
            </View>
            <View style={[styles.customerRow, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="person-outline" size={14} color={colors.textSecondary as string} />
              <Text style={[styles.customer, { textAlign: rtl.textStart }]} numberOfLines={1}>{item.customer?.name || '—'}</Text>
            </View>
          </View>
          <View style={[styles.statusBadge, { backgroundColor: `${color}20`, flexDirection: rtl.rowDirection }]}>
            <Ionicons name={(STATUS_ICONS[item.quotationStatus] || 'document-outline') as any} size={13} color={color} />
            <Text style={[styles.statusText, { color }]}>{statusLabel(item.quotationStatus)}</Text>
          </View>
        </View>

        <View style={[styles.details, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.detail, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="calendar-outline" size={15} color={colors.textSecondary as string} />
            <Text style={styles.detailText}>{formatDate(item.createdAt)}</Text>
          </View>
          <View style={[styles.detail, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="cube-outline" size={15} color={colors.textSecondary as string} />
            <Text style={styles.detailText}>{t('orders.list.itemsCount', { count: itemsCount })}</Text>
          </View>
          {!!validUntil && (
            <View style={[styles.detail, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="hourglass-outline" size={15} color={isExpired ? colors.error as string : colors.textSecondary as string} />
              <Text style={[styles.detailText, isExpired && { color: colors.error }]}>{t('quotations.list.validUntil', { date: validUntil })}</Text>
            </View>
          )}
        </View>

        <View style={[styles.footer, { flexDirection: rtl.rowDirection }]}>
          <Text style={styles.total}>{formatTotal(item.totalPrice, item.currency)}</Text>
          <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity style={[styles.actionButton, { flexDirection: rtl.rowDirection }]} onPress={() => navigateTo('quotation-detail', item._id)}>
              <Ionicons name="eye-outline" size={17} color={colors.primary as string} />
              <Text style={styles.actionText}>{t('quotations.list.view')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.actionButton, { flexDirection: rtl.rowDirection }]} onPress={() => setDocumentsFor(item)}>
              <Ionicons name="document-text-outline" size={17} color={colors.primary as string} />
              <Text style={styles.actionText}>{t('documents.title')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={scopedListName || t('quotations.list.title')}
          onBack={() => navigateTo('home')}
          actions={[
            ...(hasPermissionAtLeast('general.manageOrders', 'edit') ? [{ icon: 'documents-outline' as const, onPress: () => navigateTo('quotation-templates'), label: t('documents.templatesTitle'), accessibilityLabel: t('documents.templatesTitle') }] : []),
            { icon: 'add', primary: true, onPress: () => navigateTo('quotation-create'), label: t('quotations.list.newQuotation'), accessibilityLabel: t('quotations.list.newQuotation') },
          ]}
        />
      }
    >
      <View style={styles.container}>
        {scopedToDataListId && (
          <View style={styles.scopedBannerWrapper}>
            <ScopedListBanner
              moduleLabel={t('quotations.list.title')}
              moduleIcon="document-text-outline"
              moduleScreen="quotations"
              moduleScreenRouteParams={{}}
            />
          </View>
        )}

        <ListFilterBar
          controller={list}
          fields={quotationFields}
          sorts={quotationSorts}
          onOpenFilters={() => setShowFilters(true)}
          searchPlaceholder={t('quotations.list.searchPlaceholder')}
          resultLabel={total === 1 ? t('quotations.list.countSingular', { count: total }) : t('quotations.list.countPlural', { count: total })}
        />

        {loading && quotations.length === 0 ? (
          <View style={styles.centerContainer}><ActivityIndicator size="large" color={colors.primary as string} /></View>
        ) : quotations.length === 0 ? (
          <View style={styles.centerContainer}>
            <View style={styles.emptyIcon}>
              <Ionicons name="document-text-outline" size={40} color={colors.primary as string} />
            </View>
            <Text style={styles.emptyText}>{hasSearchOrFilters ? t('quotations.list.emptyFiltered') : t('quotations.list.empty')}</Text>
            {hasSearchOrFilters
              ? (
                <TouchableOpacity onPress={() => { list.reset(); list.setSearch(''); }}>
                  <Text style={styles.clearText}>{t('quotations.filters.clear')}</Text>
                </TouchableOpacity>
              )
              : <Text style={styles.emptySubtext}>{t('quotations.list.emptyHint')}</Text>}
          </View>
        ) : (
          <FlatList
            data={quotations}
            renderItem={renderQuotation}
            keyExtractor={(item) => item._id}
            contentContainerStyle={styles.listContent}
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.5}
            ListFooterComponent={loading && quotations.length > 0 ? (
              <View style={styles.listFooter}><ActivityIndicator size="small" color={colors.primary as string} /></View>
            ) : null}
          />
        )}
      </View>

      <FilterSheet
        visible={showFilters}
        title={t('quotations.filters.title')}
        fields={quotationFields}
        sorts={quotationSorts}
        value={list.state}
        onApply={list.apply}
        onClose={() => setShowFilters(false)}
      />
      <QuotationDocumentModal quotation={documentsFor} onClose={() => setDocumentsFor(null)} />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  scopedBannerWrapper: { paddingHorizontal: spacing.lg, paddingTop: spacing.sm },
  clearText: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.primary, marginTop: 2 },
  listContent: {
    padding: spacing.lg,
    gap: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' as any } : {}),
  },
  cardHeader: { justifyContent: 'space-between', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.sm },
  cardHeaderStart: { flex: 1, minWidth: 0 },
  numberRow: { alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs },
  number: { fontSize: typography.fontSize.lg, fontWeight: "700", color: colors.textDark, flexShrink: 1 },
  copyButton: { padding: 2 },
  customerRow: { alignItems: 'center', gap: 6 },
  customer: { fontSize: typography.fontSize.base, color: colors.textSecondary, flexShrink: 1 },
  statusBadge: { alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: 12 },
  statusText: { fontSize: typography.fontSize.xs, fontWeight: "700" },
  details: { flexWrap: 'wrap', gap: spacing.md, marginBottom: spacing.sm },
  detail: { alignItems: 'center', gap: 6 },
  detailText: { fontSize: typography.fontSize.sm, color: colors.textSecondary },
  footer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.sm,
  },
  total: { fontSize: typography.fontSize.lg, fontWeight: "700", color: colors.textDark },
  actions: { gap: spacing.xs },
  actionButton: {
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: `${colors.primary as string}12`,
  },
  actionText: { fontSize: typography.fontSize.xs, fontWeight: '600', color: colors.primary },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: `${colors.primary as string}15`,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, marginTop: spacing.md },
  emptySubtext: { fontSize: typography.fontSize.sm, color: colors.textLight, marginTop: spacing.xs },
  listFooter: { padding: spacing.md, alignItems: 'center' },
});
